import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../services/api';
import { useExportExcel } from '../hooks/useExportExcel';

// ─── Constantes Calendrier ──────────────────────────────────────────────────
const CAL_MONTHS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const CAL_DAYS   = ['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'];

const TYPE_CAL_COLORS = {
  'Congé annuel':              { bg: '#DBEAFE', text: '#1E3A8A', dot: '#3B82F6' },
  'Absence pour maladie':      { bg: '#FEE2E2', text: '#7F1D1D', dot: '#EF4444' },
  'Congé de maternité':        { bg: '#D1FAE5', text: '#064E3B', dot: '#10B981' },
  'Absences exceptionnelles':  { bg: '#EDE9FE', text: '#4C1D95', dot: '#8B5CF6' },
  'Permissions exceptionnelles':{ bg: '#FEF3C7', text: '#78350F', dot: '#F59E0B' },
  'Rendez-vous médical':       { bg: '#FCE7F3', text: '#831843', dot: '#EC4899' },
  'Autre':                     { bg: '#F3F4F6', text: '#374151', dot: '#9CA3AF' },
  'En attente':                { bg: '#F3F4F6', text: '#4B5563', dot: '#9CA3AF' },
};

function getCalColor(conge) {
  if (conge.statut === 'En attente') return TYPE_CAL_COLORS['En attente'];
  if (conge.statut === 'Rejeté')     return { bg: '#FEE2E2', text: '#7F1D1D', dot: '#EF4444' };
  return TYPE_CAL_COLORS[conge.type_absence] || TYPE_CAL_COLORS['Autre'];
}

function calDateStr(y, m, d) {
  return `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
}

// ─── Vue Calendrier Mensuel ─────────────────────────────────────────────────
function CalendrierMensuel({ conges, employes, annee: anneeDefaut, onNewDemande }) {
  const now = new Date();
  const [curYear,  setCurYear]  = useState(now.getFullYear());
  const [curMonth, setCurMonth] = useState(now.getMonth());
  const [deptFilter, setDeptFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedDay, setSelectedDay] = useState(null);

  const changeMonth = (delta) => {
    let m = curMonth + delta;
    let y = curYear;
    if (m < 0)  { m = 11; y--; }
    if (m > 11) { m = 0;  y++; }
    setCurMonth(m);
    setCurYear(y);
    setSelectedDay(null);
  };

  const daysInMonth = new Date(curYear, curMonth + 1, 0).getDate();
  const firstDay    = new Date(curYear, curMonth, 1).getDay();
  const daysInPrev  = new Date(curYear, curMonth, 0).getDate();
  const todayStr    = calDateStr(now.getFullYear(), now.getMonth() + 1, now.getDate());

  const congesFiltres = conges.filter(c => {
    if (typeFilter && c.type_absence !== typeFilter) return false;
    return true;
  });

  function getEventsForDay(ds) {
    return congesFiltres.filter(c => c.date_debut?.slice(0,10) <= ds && c.date_fin?.slice(0,10) >= ds);
  }

  const total  = Math.ceil((firstDay + daysInMonth) / 7) * 7;
  const cells  = [];

  for (let i = 0; i < total; i++) {
    let yr = curYear, mo = curMonth, day, isOther = false;
    if (i < firstDay) {
      day = daysInPrev - (firstDay - 1 - i);
      yr  = curMonth === 0 ? curYear - 1 : curYear;
      mo  = curMonth === 0 ? 11 : curMonth - 1;
      isOther = true;
    } else if (i >= firstDay + daysInMonth) {
      day = i - firstDay - daysInMonth + 1;
      yr  = curMonth === 11 ? curYear + 1 : curYear;
      mo  = curMonth === 11 ? 0 : curMonth + 1;
      isOther = true;
    } else {
      day = i - firstDay + 1;
    }
    const ds     = calDateStr(yr, mo + 1, day);
    const events = isOther ? [] : getEventsForDay(ds);
    const isToday = ds === todayStr;
    const isSel   = selectedDay === ds;
    cells.push({ day, ds, isOther, isToday, isSel, events });
  }

  const selectedEvents = selectedDay ? getEventsForDay(selectedDay) : [];
  const pendingThisMonth = conges.filter(c => {
    if (c.statut !== 'En attente') return false;
    const ds = c.date_debut?.slice(0,7);
    return ds === `${curYear}-${String(curMonth+1).padStart(2,'0')}`;
  }).length;

  return (
    <div>
      {/* ── Toolbar ── */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8, marginBottom:14 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <button className="btn btn-gray btn-sm" onClick={() => changeMonth(-1)}>‹</button>
          <span style={{ fontSize:16, fontWeight:700, color:'#1a3a5c', minWidth:160, textAlign:'center' }}>
            {CAL_MONTHS[curMonth]} {curYear}
          </span>
          <button className="btn btn-gray btn-sm" onClick={() => changeMonth(1)}>›</button>
          <button className="btn btn-gray btn-sm" onClick={() => { setCurMonth(now.getMonth()); setCurYear(now.getFullYear()); }}>
            Aujourd'hui
          </button>
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ fontSize:12, height:32, borderRadius:6, border:'1px solid #d1d5db', padding:'0 8px' }}>
            <option value="">Tous les types</option>
            {TYPES_ABSENCE.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          {pendingThisMonth > 0 && (
            <span style={{ background:'#FEF3C7', color:'#92400E', fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20 }}>
              {pendingThisMonth} en attente ce mois
            </span>
          )}
          <button className="btn btn-blue btn-sm" onClick={onNewDemande}>+ Nouvelle demande</button>
        </div>
      </div>

      {/* ── Légende ── */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:'6px 16px', marginBottom:12, paddingBottom:10, borderBottom:'1px solid #e5e7eb' }}>
        {[
          ['Congé annuel','#3B82F6'],
          ['Maladie','#EF4444'],
          ['Maternité','#10B981'],
          ['Abs. exceptionnelle','#8B5CF6'],
          ['En attente','#9CA3AF'],
          ['Rejeté','#F87171'],
        ].map(([label, dot]) => (
          <div key={label} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:'#6B7280' }}>
            <div style={{ width:9, height:9, borderRadius:'50%', background:dot, flexShrink:0 }} />
            {label}
          </div>
        ))}
      </div>

      {/* ── Grille calendrier ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:1, background:'#e5e7eb', border:'1px solid #e5e7eb', borderRadius:10, overflow:'hidden' }}>
        {CAL_DAYS.map(d => (
          <div key={d} style={{ background:'#F9FAFB', padding:'6px 0', textAlign:'center', fontSize:11, fontWeight:600, color:'#6B7280', letterSpacing:'0.04em' }}>{d}</div>
        ))}
        {cells.map(({ day, ds, isOther, isToday, isSel, events }, idx) => (
          <div
            key={idx}
            onClick={() => !isOther && setSelectedDay(isSel ? null : ds)}
            style={{
              background: isSel ? '#EFF6FF' : isToday ? '#FEF9C3' : '#fff',
              minHeight: 90,
              padding: '5px 6px',
              cursor: isOther ? 'default' : 'pointer',
              opacity: isOther ? 0.4 : 1,
              display: 'flex', flexDirection: 'column', gap: 2,
              outline: isSel ? '2px solid #3B82F6' : isToday ? '2px solid #FBBF24' : 'none',
              outlineOffset: -2,
            }}
          >
            <div style={{
              fontSize: 12, color: isOther ? '#9CA3AF' : isToday ? '#B45309' : '#374151',
              fontWeight: isToday ? 800 : 500,
              width: 22, height: 22, display:'flex', alignItems:'center', justifyContent:'center',
              borderRadius: isToday ? '50%' : 0,
              background: isToday ? '#FDE68A' : 'transparent',
              marginBottom: 2,
            }}>{day}</div>
            {events.slice(0, 3).map(ev => {
              const col = getCalColor(ev);
              return (
                <div key={ev.id} style={{
                  fontSize: 10, padding:'1px 5px', borderRadius:3,
                  background: col.bg, color: col.text,
                  whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
                  lineHeight: 1.5,
                }} title={`${ev.nom} ${ev.prenoms} — ${ev.type_absence} (${ev.statut})`}>
                  {ev.nom} {ev.prenoms?.split(' ')[0]}
                </div>
              );
            })}
            {events.length > 3 && (
              <div style={{ fontSize:10, color:'#9CA3AF', paddingLeft:4 }}>+{events.length - 3} autres</div>
            )}
          </div>
        ))}
      </div>

      {/* ── Détail du jour sélectionné ── */}
      {selectedDay && selectedEvents.length > 0 && (
        <div style={{ marginTop:16, background:'#F8FAFC', border:'1px solid #E2E8F0', borderRadius:10, padding:'14px 18px' }}>
          <div style={{ fontWeight:700, fontSize:13, color:'#1a3a5c', marginBottom:10 }}>
            📅 {new Date(selectedDay + 'T12:00:00').toLocaleDateString('fr-FR', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}
            <span style={{ marginLeft:8, fontWeight:400, fontSize:12, color:'#6B7280' }}>{selectedEvents.length} absence(s)</span>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:8 }}>
            {selectedEvents.map(ev => {
              const col = getCalColor(ev);
              const sc  = STATUT_COLORS[ev.statut] || STATUT_COLORS['En attente'];
              return (
                <div key={ev.id} style={{ background:'#fff', border:`1.5px solid ${col.dot}30`, borderLeft:`4px solid ${col.dot}`, borderRadius:8, padding:'10px 14px' }}>
                  <div style={{ fontWeight:700, fontSize:13, color:'#1a3a5c' }}>{ev.nom} {ev.prenoms}</div>
                  <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>{ev.matricule}</div>
                  <div style={{ fontSize:12, color:'#374151', marginBottom:4 }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                      <span style={{ width:8, height:8, borderRadius:'50%', background:col.dot, display:'inline-block' }} />
                      {ev.type_absence}
                    </span>
                  </div>
                  <div style={{ fontSize:11, color:'#6B7280', fontFamily:'monospace' }}>
                    {fmtDate(ev.date_debut)} → {fmtDate(ev.date_fin)} • {ev.nombre_jours}j
                  </div>
                  <div style={{ marginTop:6 }}>
                    <span style={{ background:sc.bg, color:sc.color, padding:'1px 8px', borderRadius:20, fontSize:10, fontWeight:700 }}>
                      {ev.statut}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {selectedDay && selectedEvents.length === 0 && (
        <div style={{ marginTop:12, textAlign:'center', fontSize:12, color:'#9CA3AF', padding:'10px 0' }}>
          Aucune absence enregistrée pour ce jour.
        </div>
      )}
    </div>
  );
}

// ─── Constantes ────────────────────────────────────────────────────────────
const TYPES_ABSENCE = [
  'Congé annuel',
  'Permissions exceptionnelles',
  'Absences exceptionnelles',
  'Absence pour maladie',
  'Rendez-vous médical',
  'Congé de maternité',
  'Autre',
];

const STATUT_COLORS = {
  'En attente': { bg: '#FEF3C7', color: '#92400E', dot: '#F59E0B' },
  'Approuvé':   { bg: '#D1FAE5', color: '#065F46', dot: '#10B981' },
  'Rejeté':     { bg: '#FEE2E2', color: '#991B1B', dot: '#EF4444' },
};

const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const fmtDateLong = d => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};
const moisLabels = ['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Aoû','Sep','Oct','Nov','Déc'];

// ─── Widget solde congés §3.1 ───────────────────────────────────────────────
function SoldeWidget({ employeId, annee }) {
  const [solde, setSolde]     = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!employeId) return;
    setLoading(true);
    try {
      const r = await api.get('/conges/soldes', { params: { employe_id: employeId, annee } });
      setSolde(r.data);
    } catch { setSolde(null); }
    setLoading(false);
  }, [employeId, annee]);

  useEffect(() => { load(); }, [load]);

  if (!employeId) return null;

  const items = solde ? [
    { label: 'Congé annuel',             restant: solde.restant_ca, pris: solde.pris_ca, total: solde.solde_ca, color: '#2563EB' },
    { label: 'Absence maladie',          restant: solde.restant_am, pris: solde.pris_am, total: solde.solde_am, color: '#7C3AED' },
    { label: 'Absences exceptionnelles', restant: solde.restant_ae, pris: solde.pris_ae, total: solde.solde_ae, color: '#0891B2' },
  ] : [];

  return (
    <div style={{
      background: 'linear-gradient(135deg,#EFF6FF 0%,#F0FDF4 100%)',
      border: '1.5px solid #BFDBFE', borderRadius: 12,
      padding: '14px 18px', marginBottom: 20,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <svg viewBox="0 0 24 24" width="16" height="16" fill="#2563EB">
          <path d="M19 3h-1V1h-2v2H8V1H6v2H5a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm0 18H5V8h14v13zM7 10h5v5H7z"/>
        </svg>
        <span style={{ fontWeight: 700, fontSize: 13, color: '#1E40AF' }}>Soldes de congés — {annee}</span>
        <button onClick={load} style={{ marginLeft:'auto', background:'none', border:'none', cursor:'pointer', color:'#6B7280', fontSize:11 }} title="Rafraîchir">↻</button>
      </div>
      {loading && <div style={{ textAlign:'center', fontSize:12, color:'#6B7280', padding:'8px 0' }}>Chargement…</div>}
      {!loading && solde && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
          {items.map(({ label, restant, pris, total, color }) => {
            const pct = total > 0 ? Math.min(100, Math.round((pris / total) * 100)) : 0;
            const restantNum = parseFloat(restant);
            const alert = restantNum <= 0, warn = !alert && restantNum <= 3;
            return (
              <div key={label} style={{ background:'#fff', borderRadius:8, padding:'10px 12px', border:`1px solid ${alert?'#FECACA':warn?'#FDE68A':'#E5E7EB'}` }}>
                <div style={{ fontSize:11, color:'#6B7280', marginBottom:4, fontWeight:500 }}>{label}</div>
                <div style={{ display:'flex', alignItems:'baseline', gap:4 }}>
                  <span style={{ fontSize:22, fontWeight:700, color: alert?'#DC2626':warn?'#D97706':color }}>{restantNum}</span>
                  <span style={{ fontSize:11, color:'#9CA3AF' }}>/ {total} j</span>
                </div>
                <div style={{ fontSize:10, color:'#9CA3AF', marginBottom:6 }}>{pris} j pris</div>
                <div style={{ height:4, background:'#F3F4F6', borderRadius:4, overflow:'hidden' }}>
                  <div style={{ width:`${pct}%`, height:'100%', borderRadius:4, background: alert?'#EF4444':warn?'#F59E0B':color, transition:'width .4s' }} />
                </div>
                {alert && <div style={{ fontSize:10, color:'#DC2626', marginTop:4, fontWeight:600 }}>⚠ Solde épuisé</div>}
                {warn  && <div style={{ fontSize:10, color:'#D97706', marginTop:4, fontWeight:600 }}>⚠ Solde faible</div>}
              </div>
            );
          })}
        </div>
      )}
      {!loading && !solde && (
        <div style={{ fontSize:12, color:'#6B7280', textAlign:'center', padding:'6px 0' }}>Solde non disponible pour cet employé.</div>
      )}
    </div>
  );
}

// ─── Formulaire Congé (création ET édition) ─────────────────────────────────
function CongeForm({ initialData, employes, annee, onSubmit, onCancel, saving }) {
  const initForm = (d) => ({
    employe_id:    d?.employe_id    ?? '',
    superieur_nom: d?.superieur_nom ?? '',
    type_absence:  d?.type_absence  ?? 'Congé annuel',
    type_autre:    d?.type_autre    ?? '',
    date_debut:    d?.date_debut    ? d.date_debut.slice(0,10) : '',
    date_fin:      d?.date_fin      ? d.date_fin.slice(0,10)   : '',
    nombre_jours:  d?.nombre_jours  ?? 0,
    date_reprise:  d?.date_reprise  ? d.date_reprise.slice(0,10): '',
    raison:        d?.raison        ?? '',
  });

  const [form, setForm]       = useState(() => initForm(initialData));
  const [solde, setSolde]     = useState(null);
  const [soldeLoading, setSoldeLoading] = useState(false);
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  // Calcul automatique nombre_jours
  useEffect(() => {
    if (form.date_debut && form.date_fin) {
      const d1 = new Date(form.date_debut), d2 = new Date(form.date_fin);
      const diff = Math.max(0, Math.round((d2 - d1) / 86400000) + 1);
      setForm(p => ({ ...p, nombre_jours: diff }));
    }
  }, [form.date_debut, form.date_fin]);

  // Charger le solde quand l'employé change
  useEffect(() => {
    if (!form.employe_id) { setSolde(null); return; }
    setSoldeLoading(true);
    api.get('/conges/soldes', { params: { employe_id: form.employe_id, annee } })
      .then(r => setSolde(r.data))
      .catch(() => setSolde(null))
      .finally(() => setSoldeLoading(false));
  }, [form.employe_id, annee]);

  // Solde restant après déduction (uniquement pour Congé annuel)
  const isCa       = form.type_absence === 'Congé annuel';
  const soldeCa    = solde ? parseFloat(solde.restant_ca) : null;
  const joursDemd  = parseInt(form.nombre_jours) || 0;
  const soldeApres = soldeCa !== null ? soldeCa - joursDemd : null;
  const depassement = soldeApres !== null && soldeApres < 0;

  return (
    <div className="card">
      <SoldeWidget employeId={form.employe_id ? parseInt(form.employe_id) : null} annee={annee} />

      <div style={{ fontWeight:700, fontSize:14, color:'#1a3a5c', marginBottom:14 }}>
        {initialData ? 'Modifier la demande' : 'Informations sur la demande'}
      </div>
      <div className="form-grid">
        <div className="fg">
          <label>Employé(e) *</label>
          <select value={form.employe_id} onChange={e => {
            const empId = e.target.value;
            const emp = employes.find(x => String(x.id) === String(empId));
            setForm(p => ({
              ...p,
              employe_id: empId,
              // Pré-remplit le supérieur hiérarchique désigné sur la fiche de l'employé,
              // reste modifiable manuellement juste après.
              superieur_nom: emp?.superieur_nom
                ? `${emp.superieur_nom} ${emp.superieur_prenoms || ''}`.trim() + (emp.superieur_matricule ? ` (${emp.superieur_matricule})` : '')
                : '',
            }));
          }}>
            <option value="">— Sélectionner —</option>
            {employes.map(e => <option key={e.id} value={e.id}>{e.nom} {e.prenoms} ({e.matricule})</option>)}
          </select>
        </div>
        <div className="fg">
          <label>Supérieur hiérarchique</label>
          <select value={form.superieur_nom} onChange={f('superieur_nom')}>
            <option value="">— Sélectionner —</option>
            {employes.map(e => <option key={e.id} value={`${e.nom} ${e.prenoms} (${e.matricule})`}>{e.nom} {e.prenoms} ({e.matricule})</option>)}
          </select>
        </div>
      </div>

      <div style={{ fontWeight:700, fontSize:14, color:'#1a3a5c', margin:'18px 0 10px' }}>
        Type d'absence <span style={{ fontSize:11, fontWeight:400, color:'#888' }}>(choisissez la raison correspondante)</span>
      </div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:'10px 24px', marginBottom:18 }}>
        {TYPES_ABSENCE.map(t => (
          <label key={t} style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:13 }}>
            <input type="radio" name="type_absence_form" value={t} checked={form.type_absence === t} onChange={f('type_absence')} />
            {t}
          </label>
        ))}
      </div>
      {form.type_absence === 'Autre' && (
        <div className="fg" style={{ marginBottom:14 }}>
          <label>Préciser *</label>
          <input value={form.type_autre} onChange={f('type_autre')} placeholder="Précisez le type d'absence" />
        </div>
      )}

      <div style={{ fontWeight:700, fontSize:14, color:'#1a3a5c', marginBottom:10 }}>Période d'absence</div>
      <div className="form-grid three">
        <div className="fg">
          <label>Du *</label>
          <input type="date" value={form.date_debut} onChange={f('date_debut')} />
        </div>
        <div className="fg">
          <label>Au *</label>
          <input type="date" value={form.date_fin} onChange={f('date_fin')} />
        </div>
        <div className="fg">
          <label>Nombre de jours</label>
          <input type="number" value={form.nombre_jours} readOnly style={{ background:'#f0f4f8', color:'#1565C0', fontWeight:700, cursor:'default' }} />
        </div>
      </div>

      {/* ── Récapitulatif solde CA en temps réel ── */}
      {isCa && form.employe_id && (
        <div style={{
          display:'flex', alignItems:'center', gap:0,
          background: depassement ? '#FFF1F2' : '#F0FDF4',
          border: `1.5px solid ${depassement ? '#FECDD3' : '#BBF7D0'}`,
          borderRadius:10, padding:'12px 18px', marginBottom:16,
          flexWrap:'wrap', gap:16,
        }}>
          {soldeLoading ? (
            <span style={{ fontSize:12, color:'#6B7280' }}>Chargement du solde…</span>
          ) : soldeCa === null ? (
            <span style={{ fontSize:12, color:'#6B7280' }}>Solde non disponible</span>
          ) : (
            <>
              <div style={{ textAlign:'center', minWidth:90 }}>
                <div style={{ fontSize:11, color:'#6B7280', marginBottom:2 }}>Solde actuel</div>
                <div style={{ fontSize:22, fontWeight:800, color:'#1D4ED8' }}>{soldeCa} <span style={{ fontSize:12, fontWeight:500 }}>j</span></div>
              </div>
              <div style={{ fontSize:20, color:'#9CA3AF', fontWeight:300 }}>−</div>
              <div style={{ textAlign:'center', minWidth:90 }}>
                <div style={{ fontSize:11, color:'#6B7280', marginBottom:2 }}>Jours demandés</div>
                <div style={{ fontSize:22, fontWeight:800, color:'#B45309' }}>{joursDemd} <span style={{ fontSize:12, fontWeight:500 }}>j</span></div>
              </div>
              <div style={{ fontSize:20, color:'#9CA3AF', fontWeight:300 }}>=</div>
              <div style={{ textAlign:'center', minWidth:90 }}>
                <div style={{ fontSize:11, color:'#6B7280', marginBottom:2 }}>Solde restant</div>
                <div style={{ fontSize:22, fontWeight:800, color: depassement ? '#DC2626' : '#16A34A' }}>
                  {soldeApres} <span style={{ fontSize:12, fontWeight:500 }}>j</span>
                </div>
              </div>
              {depassement && (
                <div style={{ fontSize:12, color:'#DC2626', fontWeight:600, marginLeft:'auto' }}>
                  ⚠ Dépassement de {Math.abs(soldeApres)} j — solde insuffisant
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div className="fg" style={{ marginBottom:18 }}>
        <label>Date de reprise effective du service</label>
        <input type="date" value={form.date_reprise} onChange={f('date_reprise')} />
      </div>

      <div style={{ fontWeight:700, fontSize:14, color:'#1a3a5c', marginBottom:10 }}>Raisons de l'absence</div>
      <div className="fg" style={{ marginBottom:6 }}>
        <textarea rows={4} value={form.raison} onChange={f('raison')} placeholder="Décrivez les raisons de votre absence..."
          style={{ width:'100%', padding:'10px 12px', borderRadius:6, border:'1px solid #d1d5db', fontSize:13, resize:'vertical' }} />
      </div>
      <div style={{ fontSize:11, color:'#888', marginBottom:20 }}>
        Vous devez fournir un justificatif pour les congés autres que les congés maladie, au moins deux jours avant la date d'absence.
      </div>

      <div className="actions-bar">
        <button className="btn btn-gray" onClick={onCancel}>Annuler</button>
        <button className="btn btn-blue" onClick={() => onSubmit(form)} disabled={saving}>
          {saving ? '⏳ Enregistrement…' : initialData ? '💾 Enregistrer les modifications' : '📤 Soumettre la demande'}
        </button>
      </div>
    </div>
  );
}

// ─── Modal Édition ──────────────────────────────────────────────────────────
function EditModal({ conge, employes, annee, onSave, onClose, saving }) {
  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000,
      display:'flex', alignItems:'flex-start', justifyContent:'center',
      overflowY:'auto', padding:'40px 16px',
    }} onClick={e => { if(e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:'#fff', borderRadius:12, width:'100%', maxWidth:780, padding:28, boxShadow:'0 20px 60px rgba(0,0,0,.3)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h3 style={{ margin:0, fontSize:16, color:'#1a3a5c' }}>✏️ Modifier la demande #{conge.id}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#888' }}>✕</button>
        </div>
        <CongeForm initialData={conge} employes={employes} annee={annee} onSubmit={onSave} onCancel={onClose} saving={saving} />
      </div>
    </div>
  );
}

// ─── Génération PDF certificat (jsPDF + logo embarqué) ─────────────────────
const LOGO_GFM3_B64 = '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';

async function generateCertificatPDF(conge) {
  // Charger jsPDF dynamiquement si pas encore disponible
  if (!window.jspdf) {
    await new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      s.onload = resolve; s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210, H = 297;
  const mL = 14, mR = 14, mT = 12;

  // ── Couleurs ──
  const NAVY  = [26, 58, 92];
  const GOLD  = [201, 168, 76];
  const WHITE = [255, 255, 255];
  const LIGHT = [240, 244, 255];
  const GRAY  = [107, 114, 128];
  const GREEN_BG = [209, 250, 229]; const GREEN_TXT = [6, 95, 70];
  const RED_BG   = [254, 226, 226]; const RED_TXT   = [153, 27, 27];
  const YEL_BG   = [254, 243, 199]; const YEL_TXT   = [146, 64, 14];

  const setColor  = (rgb, type='fill') => type==='fill' ? doc.setFillColor(...rgb) : doc.setTextColor(...rgb);
  const setDraw   = (rgb) => doc.setDrawColor(...rgb);
  const fmtD      = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
  const fmtDLong  = d => d ? new Date(d).toLocaleDateString('fr-FR', { day:'numeric', month:'long', year:'numeric' }) : '—';
  const ref       = 'C-' + String(conge.id).padStart(5,'0');
  let y = mT;

  // ══ Cadre extérieur navy ══
  setDraw(NAVY); doc.setLineWidth(0.8);
  doc.rect(mL, y, W - mL - mR, H - mT - 12);
  // Cadre intérieur gold
  setDraw(GOLD); doc.setLineWidth(0.3);
  doc.rect(mL+2.5, y+2.5, W - mL - mR - 5, H - mT - 17);

  y += 6;

  // ══ Logo GFM3 (haut gauche) ══
  try {
    doc.addImage(LOGO_GFM3_B64, 'JPEG', mL+5, y, 22, 20);
  } catch(e) { console.warn('Logo non chargé', e); }

  // ══ Entête texte (à droite du logo) ══
  setColor(NAVY, 'text'); doc.setFont('helvetica','bold'); doc.setFontSize(13);
  doc.text('GÉNÉRATION FEMME DU 3ème MILLÉNAIRE', mL+30, y+7);
  doc.setFont('helvetica','normal'); doc.setFontSize(8.5); setColor(GRAY,'text');
  doc.text('Système de Gestion des Ressources Humaines', mL+30, y+12);
  doc.text(`Réf. : ${ref}`, mL+30, y+17);

  // Date + statut (haut droite)
  doc.setFontSize(8); setColor(GRAY,'text');
  doc.text(`Émis le : ${fmtDLong(new Date().toISOString())}`, W - mR - 5, y+7, { align:'right' });

  // Badge statut
  const statutW = 28, statutH = 6, statutX = W - mR - 5 - statutW, statutY = y+10;
  const sBg  = conge.statut==='Approuvé' ? GREEN_BG : conge.statut==='Rejeté' ? RED_BG  : YEL_BG;
  const sTxt = conge.statut==='Approuvé' ? GREEN_TXT: conge.statut==='Rejeté' ? RED_TXT : YEL_TXT;
  setColor(sBg); doc.roundedRect(statutX, statutY, statutW, statutH, 1.5, 1.5, 'F');
  setDraw(sTxt); doc.setLineWidth(0.2); doc.roundedRect(statutX, statutY, statutW, statutH, 1.5, 1.5, 'S');
  setColor(sTxt,'text'); doc.setFont('helvetica','bold'); doc.setFontSize(7.5);
  doc.text(conge.statut.toUpperCase(), statutX + statutW/2, statutY + 4, { align:'center' });

  y += 26;

  // ══ Séparateur navy ══
  setDraw(NAVY); doc.setLineWidth(0.5); doc.line(mL+5, y, W-mR-5, y); y+=2;
  setDraw(GOLD); doc.setLineWidth(0.2); doc.line(mL+5, y, W-mR-5, y); y+=6;

  // ══ Titre central ══
  const titreW = 110, titreH = 16, titreX = (W - titreW)/2;
  setDraw(NAVY); doc.setLineWidth(0.5); doc.rect(titreX, y, titreW, titreH);
  setColor(NAVY,'text'); doc.setFont('helvetica','bold'); doc.setFontSize(15);
  doc.text('CERTIFICAT DE CONGÉ', W/2, y+8, { align:'center' });
  doc.setFont('helvetica','italic'); doc.setFontSize(8); setColor(GOLD,'text');
  doc.text('Certificate of Leave', W/2, y+13, { align:'center' });
  y += 22;

  // ══ Intro ══
  doc.setFont('helvetica','italic'); doc.setFontSize(8.5); setColor(GRAY,'text');
  doc.text('Le présent document atteste officiellement de la demande de congé / d\'absence enregistrée dans le système RH.',
    W/2, y, { align:'center' }); y+=8;
  setDraw(GOLD); doc.setLineWidth(0.2); doc.line(mL+5, y, W-mR-5, y); y+=6;

  // ══ Grille infos employé ══
  const colW = (W - mL - mR - 10 - 8) / 2;
  const drawInfoCell = (label, value, cx, cy) => {
    setColor(NAVY,'text'); doc.setFont('helvetica','bold'); doc.setFontSize(7);
    doc.text(label.toUpperCase(), cx, cy);
    setColor([30,30,46],'text'); doc.setFont('helvetica','normal'); doc.setFontSize(10);
    doc.text(String(value || '—'), cx, cy+5);
    setDraw([229,231,235]); doc.setLineWidth(0.2); doc.line(cx, cy+6.5, cx+colW, cy+6.5);
  };

  drawInfoCell('Nom & Prénoms', `${conge.nom} ${conge.prenoms}`, mL+5, y);
  drawInfoCell('Matricule', conge.matricule || '—', mL+5+colW+8, y);
  y += 14;
  drawInfoCell('Type d\'absence', `${conge.type_absence}${conge.type_autre?' — '+conge.type_autre:''}`, mL+5, y);
  drawInfoCell('Supérieur hiérarchique', conge.superieur_nom || '—', mL+5+colW+8, y);
  y += 18;

  // ══ Bloc période ══
  const pBoxH = 26;
  setColor(LIGHT); doc.roundedRect(mL+5, y, W-mL-mR-10, pBoxH, 2, 2, 'F');
  setDraw(NAVY); doc.setLineWidth(0.4); doc.roundedRect(mL+5, y, W-mL-mR-10, pBoxH, 2, 2, 'S');

  // Cercle jours (droite)
  const cxC = W-mR-20, cyC = y+pBoxH/2, rC = 11;
  setColor(NAVY); doc.circle(cxC, cyC, rC, 'F');
  setColor(WHITE,'text'); doc.setFont('helvetica','bold'); doc.setFontSize(14);
  doc.text(String(conge.nombre_jours), cxC, cyC+1, { align:'center' });
  doc.setFontSize(6.5);
  doc.text(`JOUR${conge.nombre_jours>1?'S':''}`, cxC, cyC+5.5, { align:'center' });

  // Dates
  const dStartX = mL+18, dEndX = mL+18+55;
  setColor(NAVY,'text'); doc.setFont('helvetica','bold'); doc.setFontSize(7);
  doc.text('DATE DE DÉBUT', dStartX, y+7);
  setColor([30,30,46],'text'); doc.setFont('helvetica','bold'); doc.setFontSize(11);
  doc.text(fmtDLong(conge.date_debut), dStartX, y+14);

  setColor(GOLD,'text'); doc.setFont('helvetica','normal'); doc.setFontSize(16);
  doc.text('→', dStartX+52, y+14);

  setColor(NAVY,'text'); doc.setFont('helvetica','bold'); doc.setFontSize(7);
  doc.text('DATE DE FIN', dEndX+14, y+7);
  setColor([30,30,46],'text'); doc.setFont('helvetica','bold'); doc.setFontSize(11);
  doc.text(fmtDLong(conge.date_fin), dEndX+14, y+14);

  y += pBoxH + 6;

  // Date de reprise
  if (conge.date_reprise) {
    setColor(NAVY,'text'); doc.setFont('helvetica','bold'); doc.setFontSize(7);
    doc.text('DATE DE REPRISE DU SERVICE', mL+5, y);
    setColor([30,30,46],'text'); doc.setFont('helvetica','normal'); doc.setFontSize(10);
    doc.text(fmtDLong(conge.date_reprise), mL+5, y+5);
    setDraw([229,231,235]); doc.setLineWidth(0.2); doc.line(mL+5, y+6.5, W-mR-5, y+6.5);
    y += 12;
  }

  // ══ Motif ══
  if (conge.raison) {
    setColor(NAVY,'text'); doc.setFont('helvetica','bold'); doc.setFontSize(7);
    doc.text('MOTIF / RAISON', mL+5, y); y+=4;
    setDraw([209,213,219]); doc.setLineWidth(0.2);
    doc.roundedRect(mL+5, y, W-mL-mR-10, 16, 1, 1, 'S');
    setColor([55,65,81],'text'); doc.setFont('helvetica','italic'); doc.setFontSize(9);
    const lines = doc.splitTextToSize(conge.raison, W-mL-mR-18);
    doc.text(lines.slice(0,2), mL+8, y+5);
    y += 20;
  }

  setDraw(GOLD); doc.setLineWidth(0.2); doc.line(mL+5, y, W-mR-5, y); y+=6;

  // ══ Attestation ══
  const attestation = `Le Département des Ressources Humaines certifie que l'employé(e) ${conge.nom} ${conge.prenoms} (${conge.matricule||'—'}) a soumis une demande de ${conge.type_absence.toLowerCase()} pour la période allant du ${fmtDLong(conge.date_debut)} au ${fmtDLong(conge.date_fin)}, soit ${conge.nombre_jours} jour${conge.nombre_jours>1?'s':''} calendaire${conge.nombre_jours>1?'s':''}. ${conge.statut==='Approuvé'?'Cette demande a été approuvée par la hiérarchie.':conge.statut==='Rejeté'?'Cette demande a été rejetée par la hiérarchie.':'Cette demande est actuellement en cours de traitement.'}`;
  setColor([55,65,81],'text'); doc.setFont('helvetica','normal'); doc.setFontSize(9);
  const attLines = doc.splitTextToSize(attestation, W-mL-mR-14);
  doc.text(attLines, mL+10, y, { lineHeightFactor: 1.6 });
  y += attLines.length * 5.5 + 8;

  // ══ Signatures ══
  const sigY = Math.max(y, H - 60);
  const sigW = (W - mL - mR - 18) / 2;

  // Employé
  setColor(NAVY,'text'); doc.setFont('helvetica','bold'); doc.setFontSize(7.5);
  doc.text("L'EMPLOYÉ(E)", mL+5 + sigW/2, sigY, { align:'center' });
  setDraw([55,65,81]); doc.setLineWidth(0.3);
  doc.line(mL+10, sigY+18, mL+5+sigW-5, sigY+18);
  setColor(GRAY,'text'); doc.setFont('helvetica','normal'); doc.setFontSize(8);
  doc.text(`${conge.nom} ${conge.prenoms}`, mL+5+sigW/2, sigY+22, { align:'center' });
  doc.text(conge.matricule||'', mL+5+sigW/2, sigY+26, { align:'center' });

  // RH/Direction
  const sig2X = mL+5+sigW+8;
  setColor(NAVY,'text'); doc.setFont('helvetica','bold'); doc.setFontSize(7.5);
  doc.text('VALIDATION RH / DIRECTION', sig2X+sigW/2, sigY, { align:'center' });
  // Cercle cachet
  setDraw([209,213,219]); doc.setLineWidth(0.4);
  doc.circle(sig2X+sigW/2, sigY+11, 9, 'S');
  setColor(GRAY,'text'); doc.setFont('helvetica','normal'); doc.setFontSize(6.5);
  doc.text('CACHET', sig2X+sigW/2, sigY+9.5, { align:'center' });
  doc.text('SIGNATURE', sig2X+sigW/2, sigY+13, { align:'center' });
  if (conge.approuve_par) {
    doc.setFontSize(7.5);
    doc.text(`Approuvé par : ${conge.approuve_par}`, sig2X+sigW/2, sigY+24, { align:'center' });
  }

  // ══ Pied de page ══
  setDraw(NAVY); doc.setLineWidth(0.4);
  doc.line(mL+5, H-17, W-mR-5, H-17);
  setColor(GRAY,'text'); doc.setFont('helvetica','normal'); doc.setFontSize(7);
  doc.text(`Document généré automatiquement par GFM3 RH  •  Réf. ${ref}  •  ${new Date().toLocaleDateString('fr-FR')}`, W/2, H-13, { align:'center' });
  doc.text('Ce document est fourni à titre informatif et doit être contresigné par le service RH pour être officiel.', W/2, H-9, { align:'center' });

  // ══ Télécharger ══
  doc.save(`Certificat_conge_${conge.matricule||conge.id}_${ref}.pdf`);
}


// ─── Modal de confirmation suppression ──────────────────────────────────────
function DeleteModal({ conge, onConfirm, onCancel, loading }) {
  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center',
    }} onClick={e => { if(e.target===e.currentTarget) onCancel(); }}>
      <div style={{ background:'#fff', borderRadius:12, width:440, padding:28, boxShadow:'0 20px 60px rgba(0,0,0,.3)' }}>
        <div style={{ textAlign:'center', marginBottom:16 }}>
          <div style={{ fontSize:40, marginBottom:8 }}>🗑️</div>
          <h3 style={{ margin:0, color:'#1a3a5c', fontSize:18 }}>Supprimer la demande</h3>
        </div>
        <p style={{ textAlign:'center', color:'#374151', fontSize:13, lineHeight:1.6, marginBottom:20 }}>
          Voulez-vous vraiment supprimer la demande de <strong>{conge.nom} {conge.prenoms}</strong> ({conge.matricule}) ?<br/>
          <span style={{ color:'#6B7280' }}>{conge.type_absence} • {fmtDate(conge.date_debut)} → {fmtDate(conge.date_fin)}</span>
        </p>
        <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
          <button className="btn btn-gray" onClick={onCancel} disabled={loading}>Annuler</button>
          <button
            onClick={onConfirm} disabled={loading}
            style={{ padding:'8px 20px', background:'#DC2626', color:'#fff', border:'none', borderRadius:6, fontWeight:700, cursor:'pointer', fontSize:13 }}
          >{loading ? '⏳ Suppression…' : '🗑 Confirmer la suppression'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Composant principal ────────────────────────────────────────────────────

// ── Hook tri tableau ──────────────────────────────────────────
function useSortTable(data, defaultCol = '') {
  const [sortCol, setSortCol] = React.useState(defaultCol);
  const [sortDir, setSortDir] = React.useState('asc');
  const handleSort = col => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };
  const sorted = React.useMemo(() => {
    if (!sortCol || !data) return data || [];
    return [...data].sort((a, b) => {
      const va = (a[sortCol] ?? '').toString();
      const vb = (b[sortCol] ?? '').toString();
      const cmp = va.localeCompare(vb, 'fr', { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortCol, sortDir]);
  const Th = ({ col, label, style = {}, className = '' }) => (
    <th onClick={() => handleSort(col)}
        className={className}
        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
        title={`Trier par ${label}`}>
      {label}{' '}
      {sortCol === col
        ? (sortDir === 'asc' ? '▲' : '▼')
        : <span style={{ opacity: 0.25 }}>⇅</span>}
    </th>
  );
  return { sorted, Th, sortCol, sortDir };
}

export default function CongesPage() {
  const [tab, setTab]           = useState('calendar');
  const [conges, setConges]     = useState([]);
  const [employes, setEmployes] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [toast, setToast]       = useState(null);
  const { exporting, exportToExcel } = useExportExcel();

  // Filtres liste
  const [filterStatut, setFilterStatut] = useState('');
  const [filterMois, setFilterMois]     = useState('');
  const [filterAnnee, setFilterAnnee]   = useState(new Date().getFullYear().toString());
  const [searchQ, setSearchQ]           = useState('');

  // Modals
  const [editConge,   setEditConge]   = useState(null);   // objet congé à éditer
  const [deleteConge, setDeleteConge] = useState(null);   // objet congé à supprimer

  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState(false);

  const annee = new Date().getFullYear();

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleExport = () => {
    const moisLabel = filterMois ? ['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Aoû','Sep','Oct','Nov','Déc'][+filterMois - 1] : 'Tous';
    exportToExcel({
      rows: congesFiltres,
      columns: [
        { key: 'matricule',    label: 'Matricule',    width: 14 },
        { key: 'prenoms',      label: 'Prénoms',      width: 22 },
        { key: 'nom',          label: 'Nom',          width: 18 },
        { key: 'type_absence', label: 'Type',         width: 26 },
        { key: 'date_debut',   label: 'Début',        width: 14, format: v => fmtDate(v) },
        { key: 'date_fin',     label: 'Fin',          width: 14, format: v => fmtDate(v) },
        { key: 'nombre_jours', label: 'Jours',        width: 8  },
        { key: 'statut',       label: 'Statut',       width: 14 },
        { key: 'motif',        label: 'Motif',        width: 30 },
      ],
      filename: `GFM3_Conges_${filterAnnee}_${moisLabel}.xlsx`,
      sheetName: 'Congés & Absences',
      title: `Congés & Absences — ${moisLabel} ${filterAnnee}`,
    });
  };

  // Tous les congés pour le calendrier (sans filtres de liste)
  const [calConges, setCalConges] = useState([]);
  const loadCalConges = useCallback(async () => {
    try {
      const r = await api.get('/conges', { params: { limit: 1000 } });
      setCalConges(Array.isArray(r.data) ? r.data : []);
    } catch { setCalConges([]); }
  }, []);

  const loadConges = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatut) params.statut = filterStatut;
      if (filterMois)   params.mois   = filterMois;
      if (filterAnnee)  params.annee  = filterAnnee;
      if (searchQ)      params.q      = searchQ;
      const r = await api.get('/conges', { params });
      setConges(Array.isArray(r.data) ? r.data : []);
    } catch { setConges([]); }
    setLoading(false);
  }, [filterStatut, filterMois, filterAnnee, searchQ]);

  useEffect(() => { loadConges(); }, [loadConges]);
  useEffect(() => { loadCalConges(); }, [loadCalConges]);

  useEffect(() => {
    api.get('/employes', { params: { limit: 200 } })
       .then(r => setEmployes(r.data?.data || []))
       .catch(() => {});
  }, []);

  // Créer
  const handleCreate = async (form) => {
    if (!form.employe_id || !form.type_absence || !form.date_debut || !form.date_fin) {
      showToast('Veuillez remplir tous les champs requis.', 'error');
      return;
    }
    setSaving(true);
    try {
      await api.post('/conges', form);
      showToast('Demande soumise avec succès ✓');
      setTab('list');
      loadConges();
      loadCalConges();
    } catch (err) {
      showToast(err?.response?.data?.error || 'Erreur lors de la soumission', 'error');
    }
    setSaving(false);
  };

  // Éditer
  const handleUpdate = async (form) => {
    if (!form.type_absence || !form.date_debut || !form.date_fin) {
      showToast('Veuillez remplir tous les champs requis.', 'error');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/conges/${editConge.id}`, form);
      showToast('Demande modifiée avec succès ✓');
      setEditConge(null);
      loadConges();
      loadCalConges();
    } catch (err) {
      showToast(err?.response?.data?.error || 'Erreur lors de la modification', 'error');
    }
    setSaving(false);
  };

  // Approuver / Rejeter
  const handleApprouver = async (id) => {
    try {
      await api.put(`/conges/${id}/approuver`);
      showToast('Demande approuvée ✓');
      loadConges();
      loadCalConges();
    } catch { showToast('Erreur', 'error'); }
  };

  const handleRejeter = async (id) => {
    try {
      await api.put(`/conges/${id}/rejeter`);
      showToast('Demande rejetée');
      loadConges();
      loadCalConges();
    } catch { showToast('Erreur', 'error'); }
  };

  // Supprimer
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/conges/${deleteConge.id}`);
      showToast('Demande supprimée');
      setDeleteConge(null);
      loadConges();
      loadCalConges();
    } catch (err) {
      showToast(err?.response?.data?.error || 'Erreur lors de la suppression', 'error');
    }
    setDeleting(false);
  };

  // Filtrage local complémentaire (matricule / nom)
  const congesFiltres = conges.filter(c => {
    if (!searchQ) return true;
    const q = searchQ.toLowerCase();
    return (
      c.nom?.toLowerCase().includes(q) ||
      c.prenoms?.toLowerCase().includes(q) ||
      c.matricule?.toLowerCase().includes(q)
    );
  })
  const { sorted: sortedConges, Th: ThC } = useSortTable(congesFiltres);;

  return (
    <>
      {/* Toast */}
      {toast && (
        <div style={{
          position:'fixed', top:20, right:20, zIndex:9999,
          background: toast.type==='error' ? '#DC2626' : '#059669',
          color:'#fff', padding:'10px 18px', borderRadius:8,
          fontSize:13, fontWeight:600, boxShadow:'0 4px 12px rgba(0,0,0,.2)',
          animation:'slideIn .3s ease',
        }}>
          {toast.msg}
        </div>
      )}

      {/* Modal Édition */}
      {editConge && (
        <EditModal
          conge={editConge}
          employes={employes}
          annee={annee}
          onSave={handleUpdate}
          onClose={() => setEditConge(null)}
          saving={saving}
        />
      )}

      {/* Modal Suppression */}
      {deleteConge && (
        <DeleteModal
          conge={deleteConge}
          onConfirm={handleDelete}
          onCancel={() => setDeleteConge(null)}
          loading={deleting}
        />
      )}

      {/* En-tête */}
      <div className="page-header">
        <div>
          <h2>Congés &amp; Absences</h2>
          <p>Gérer les demandes de congé et absences</p>
        </div>
        <button className="btn btn-blue" onClick={() => setTab('new')}>
          + Nouvelle demande
        </button>
      </div>

      {/* Onglets */}
      <div className="sub-tabs">
        <button className={`sub-tab${tab==='calendar'?' active':''}`} onClick={() => setTab('calendar')}>
          📅 Calendrier
        </button>
        <button className={`sub-tab${tab==='list'?' active':''}`} onClick={() => setTab('list')}>
          Liste des demandes
          {conges.filter(c => c.statut==='En attente').length > 0 && (
            <span className="nav-badge" style={{ marginLeft:6 }}>
              {conges.filter(c => c.statut==='En attente').length}
            </span>
          )}
        </button>
        <button className={`sub-tab${tab==='new'?' active':''}`} onClick={() => setTab('new')}>
          Nouvelle demande
        </button>
      </div>

      {/* ══════════════ CALENDRIER ══════════════ */}
      {tab === 'calendar' && (
        <div className="card">
          <CalendrierMensuel
            conges={calConges}
            employes={employes}
            annee={annee}
            onNewDemande={() => setTab('new')}
          />
        </div>
      )}

      {/* ══════════════ LISTE ══════════════ */}
      {tab==='list' && (
        <div className="card">
          {/* Barre filtres + recherche */}
          <div className="crud-bar" style={{ marginBottom:16, flexWrap:'wrap', gap:8, alignItems:'center' }}>
            {/* Recherche matricule / nom */}
            <div style={{ position:'relative', flex:'1 1 200px', minWidth:180, maxWidth:300 }}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="#9CA3AF"
                style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}>
                <path d="M10 2a8 8 0 1 0 4.906 14.32l4.387 4.387 1.414-1.414-4.387-4.387A8 8 0 0 0 10 2zm0 2a6 6 0 1 1 0 12A6 6 0 0 1 10 4z"/>
              </svg>
              <input
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                placeholder="Rechercher matricule, nom…"
                style={{ paddingLeft:30, width:'100%', height:36, borderRadius:6, border:'1px solid #d1d5db', fontSize:13 }}
              />
              {searchQ && (
                <button onClick={() => setSearchQ('')}
                  style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#9CA3AF', fontSize:14 }}>
                  ✕
                </button>
              )}
            </div>

            <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)} style={{ minWidth:130 }}>
              <option value="">Tous les statuts</option>
              <option>En attente</option>
              <option>Approuvé</option>
              <option>Rejeté</option>
            </select>
            <select value={filterMois} onChange={e => setFilterMois(e.target.value)} style={{ minWidth:110 }}>
              <option value="">Tous les mois</option>
              {moisLabels.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
            </select>
            <select value={filterAnnee} onChange={e => setFilterAnnee(e.target.value)} style={{ minWidth:90 }}>
              {[annee-1, annee, annee+1].map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <button
              onClick={handleExport}
              disabled={exporting || congesFiltres.length === 0}
              style={{
                marginLeft:'auto', display:'flex', alignItems:'center', gap:6,
                background: congesFiltres.length === 0 ? '#e5e7eb' : '#16a34a',
                color: congesFiltres.length === 0 ? '#9ca3af' : '#fff',
                border:'none', borderRadius:6, padding:'0 14px', height:36,
                fontWeight:600, fontSize:13, cursor: congesFiltres.length === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              {exporting ? '⏳ Export…' : '📊 Exporter Excel'}
            </button>
            <span style={{ fontSize:12, color:'#666' }}>{congesFiltres.length} demande(s)</span>
          </div>

          {/* Table */}
          <div className="table-wrap">
            {loading ? (
              <div className="empty"><span className="spinner spinner-dark" /></div>
            ) : congesFiltres.length === 0 ? (
              <div className="empty">Aucune demande enregistrée</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <ThC col="nom"           label="Employé(e)" />
                    <ThC col="type_absence"  label="Type" />
                    <ThC col="date_debut"    label="Période" />
                    <ThC col="nb_jours"      label="Jours" />
                    <ThC col="statut"        label="Statut" />
                    <th style={{ textAlign:'center', minWidth:220 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedConges.map(c => {
                    const sc = STATUT_COLORS[c.statut] || STATUT_COLORS['En attente'];
                    return (
                      <tr key={c.id}>
                        <td>
                          <div style={{ fontWeight:700 }}>{c.nom} {c.prenoms}</div>
                          <div style={{ fontSize:11, color:'#888' }}>{c.matricule}</div>
                        </td>
                        <td style={{ fontSize:12 }}>{c.type_absence}{c.type_autre ? ` — ${c.type_autre}` : ''}</td>
                        <td style={{ fontSize:12, fontFamily:'monospace' }}>
                          {fmtDate(c.date_debut)} → {fmtDate(c.date_fin)}
                        </td>
                        <td style={{ textAlign:'center', fontWeight:700 }}>{c.nombre_jours}</td>
                        <td>
                          <span style={{
                            background:sc.bg, color:sc.color,
                            padding:'2px 10px', borderRadius:20,
                            fontSize:11, fontWeight:700, display:'inline-flex', alignItems:'center', gap:4,
                          }}>
                            <span style={{ width:6, height:6, borderRadius:'50%', background:sc.dot, display:'inline-block' }} />
                            {c.statut}
                          </span>
                        </td>
                        <td>
                          <div style={{ display:'flex', gap:5, justifyContent:'center', flexWrap:'wrap' }}>
                            {/* Approuver / Rejeter — uniquement En attente */}
                            {c.statut === 'En attente' && (
                              <>
                                <button className="btn btn-green btn-sm" onClick={() => handleApprouver(c.id)} title="Approuver">✓</button>
                                <button className="btn btn-red btn-sm" onClick={() => handleRejeter(c.id)} title="Rejeter">✕</button>
                              </>
                            )}

                            {/* Éditer */}
                            <button
                              className="btn btn-sm"
                              style={{ background:'#EFF6FF', color:'#1D4ED8', border:'1px solid #BFDBFE' }}
                              onClick={() => setEditConge(c)}
                              title="Modifier"
                            >✏️</button>

                            {/* Certificat PDF */}
                            <button
                              className="btn btn-sm"
                              style={{ background:'#F0FDF4', color:'#15803D', border:'1px solid #BBF7D0' }}
                              onClick={() => generateCertificatPDF(c)}
                              title="Générer le certificat PDF"
                            >📄 PDF</button>

                            {/* Supprimer */}
                            <button
                              className="btn btn-sm"
                              style={{ background:'#FEF2F2', color:'#DC2626', border:'1px solid #FECACA' }}
                              onClick={() => setDeleteConge(c)}
                              title="Supprimer"
                            >🗑</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ══════════════ NOUVELLE DEMANDE ══════════════ */}
      {tab === 'new' && (
        <CongeForm
          initialData={null}
          employes={employes}
          annee={annee}
          onSubmit={handleCreate}
          onCancel={() => setTab('list')}
          saving={saving}
        />
      )}
    </>
  );
}
